<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import {
    Phone,
    Trash2,
    ExternalLink,
    MoreVertical,
    Settings,
    LoaderCircle
  } from 'lucide-vue-next'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Badge } from '@/components/ui/badge'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse, FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import {
    CallsService,
    type TCall,
    type CustomerCallResult,
    OrganisationsService,
    type CustomerOrganisation,
    ClassificationEnum,
    PhoneNumbersService,
    type CustomerPhoneNumber,
    UsersService,
    RoleEnum
  } from '@/types/generated'
  import { useRoute, useRouter } from 'vue-router'
  import { useTranslation } from '@/composables/useTranslation'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import { ROUTES } from '@/router/routes.ts'
  import PhonebotSetupDialog from '@/components/app/calls/PhonebotSetupDialog.vue'
  import CustomerService from '@/components/svg/Illustrations/CustomerService.vue'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'

  const route = useRoute()
  const router = useRouter()

  const { translateLawField } = useLawFieldTranslation()
  const { translateLawTopic } = useLawTopicTranslation()

  const { t } = useTranslation({
    en: {
      Calls: 'Calls',
      TimeStamp: 'Time Stamp',
      Type: 'Type',
      Phone: 'Phone',
      FieldTopic: 'Field/Topic',
      Duration: 'Duration',
      Recording: 'Recording',
      Action: 'Action',
      ViewInquiry: 'Inquiry',
      Inquiry: 'Show inquiry',
      ViewDetails: 'Show details',
      Unknown: 'Unknown',
      pageDescription: 'Manage incoming calls and inquiries',
      NotCompleted: 'Not Completed',
      ExistingClient: 'Existing Client',
      NewClient: 'New Client',
      GeneralEnquiry: 'General Enquiry',
      Question: 'Question',
      LawFirmPartner: 'Law Firm Partner',
      Delete: 'Delete',
      HasInquiry: 'Has associated inquiry',
      CallForwarded: 'Call Forwarded',
      WithoutMessage: 'Without Message',
      Court: 'Court',
      Spam: 'Spam',
      AllTypes: 'All Types',
      LawField: 'Law Field',
      YourPhoneNumber: 'Your Phone Number',
      YourPhoneNumbers: 'Your Phone Numbers',
      GoToSettings: 'Go to Settings',
      Clerk: 'Clerk',
      notAssigned: 'Not assigned',
      emptyStateTitle: 'Set up your phone bot to get started',
      emptyStateDescription:
        'Your phone bot is not yet configured.\nComplete the setup to automatically handle calls.',
      setupNow: 'Set up now'
    },
    de: {
      Calls: 'Anrufe',
      TimeStamp: 'Eingegangen',
      Type: 'Typ',
      Phone: 'Telefonnummer',
      FieldTopic: 'Rechtsgebiet/Thema',
      Duration: 'Dauer',
      Recording: 'Aufnahme',
      Action: 'Aktion',
      ViewInquiry: 'Anfrage',
      Inquiry: 'Zur Anfrage',
      ViewDetails: 'Zum Anruf',
      Unknown: 'Unbekannt',
      pageDescription: 'Verwalten Sie eingehende Anrufe und Anfragen',
      NotCompleted: 'Unvollständig',
      ExistingClient: 'Bestandsmandant',
      NewClient: 'Neumandant',
      GeneralEnquiry: 'Allgemeine Anfrage',
      Question: 'Frage',
      LawFirmPartner: 'Geschäftspartner',
      Delete: 'Löschen',
      HasInquiry: 'Hat zugeordnete Anfrage',
      CallForwarded: 'Anruf weitergeleitet',
      WithoutMessage: 'Ohne Nachricht',
      Court: 'Gericht',
      Spam: 'Spam',
      AllTypes: 'Alle Typen',
      LawField: 'Rechtsgebiet',
      YourPhoneNumber: 'Ihre Telefonnummer',
      YourPhoneNumbers: 'Ihre Telefonnummern',
      GoToSettings: 'Zu den Einstellungen',
      Clerk: 'Sachbearbeiter',
      notAssigned: 'Nicht zugewiesen',
      emptyStateTitle: 'Richten Sie Ihren Phonebot ein, um zu beginnen',
      emptyStateDescription:
        'Ihr Phonebot ist noch nicht konfiguriert.\nVervollständigen Sie die Einrichtung, um Anrufe automatisch zu bearbeiten.',
      setupNow: 'Jetzt einrichten'
    },
    fr: {
      Calls: 'Appels',
      TimeStamp: 'Horodatage',
      Type: 'Type',
      Phone: 'Téléphone',
      FieldTopic: 'Domaine/Sujet',
      Duration: 'Durée',
      Recording: 'Enregistrement',
      Action: 'Action',
      ViewInquiry: 'Demande',
      Inquiry: 'Voir la demande',
      ViewDetails: 'Voir les détails',
      Unknown: 'Inconnu',
      pageDescription: 'Gérer les appels entrants et les demandes',
      NotCompleted: 'Non terminé',
      ExistingClient: 'Client existant',
      NewClient: 'Nouveau client',
      GeneralEnquiry: 'Demande générale',
      Question: 'Question',
      LawFirmPartner: 'Partenaire du cabinet',
      Delete: 'Supprimer',
      HasInquiry: 'A une demande associée',
      CallForwarded: 'Appel transféré',
      WithoutMessage: 'Sans message',
      Court: 'Tribunal',
      Spam: 'Spam',
      AllTypes: 'Tous les types',
      LawField: 'Domaine juridique',
      YourPhoneNumber: 'Votre numéro de téléphone',
      YourPhoneNumbers: 'Vos numéros de téléphone',
      GoToSettings: 'Aller aux paramètres',
      Clerk: 'Collaborateur',
      notAssigned: 'Non attribué',
      emptyStateTitle: 'Configurez votre bot téléphonique pour commencer',
      emptyStateDescription:
        "Votre bot téléphonique n'est pas encore configuré.\nComplétez la configuration pour traiter automatiquement les appels.",
      setupNow: 'Configurer maintenant'
    }
  })

  const isVoiceStandAlone = ref(false)
  const isLoading = ref(false)
  const selectedLawField = ref('')
  const selectedClassifications = ref('')
  const searchQuery = ref('')
  const tableRef = ref()
  const org = ref<CustomerOrganisation>()
  const phoneNumbers = ref<CustomerPhoneNumber[]>([])
  const totalCalls = ref<number | null>(null)

  const initialPage = computed(() => {
    const page = Number(route.query.page)
    return Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1
  })

  const handlePageChange = (page: number) => {
    router.replace({ query: { ...route.query, page: page > 1 ? page.toString() : undefined } })
  }

  const classificationOptions = [
    { value: ClassificationEnum.EXISTING_CLIENT_CALL_BACK, label: 'ExistingClient' },
    { value: ClassificationEnum.NEW_CLIENT_CALL, label: 'NewClient' },
    { value: ClassificationEnum.GENERAL_ENQUIRY, label: 'GeneralEnquiry' },
    { value: ClassificationEnum.QUESTION, label: 'Question' },
    { value: ClassificationEnum.NOT_COMPLETED, label: 'NotCompleted' },
    { value: ClassificationEnum.LAWFIRM_PARTNER, label: 'LawFirmPartner' },
    { value: ClassificationEnum.CALL_FORWARDED, label: 'CallForwarded' },
    { value: ClassificationEnum.WITHOUT_MESSAGE, label: 'WithoutMessage' },
    { value: ClassificationEnum.COURT, label: 'Court' },
    { value: ClassificationEnum.SPAM, label: 'Spam' }
  ]

  const lawFieldOptions = computed(() => {
    if (!org.value?.law_fields) return []
    return (org.value.law_fields as Array<string>).map((value) => ({
      value,
      title: translateLawField(value)
    }))
  })

  const filterableFields = computed(() => [
    {
      key: 'classification',
      label: t('Type'),
      type: 'multi-select' as const,
      value: selectedClassifications.value,
      options: [
        { label: t('AllTypes'), value: '__all__' },
        ...classificationOptions
          .map((option) => ({
            label: t(option.label),
            value: option.value
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      ]
    },
    {
      key: 'lawField',
      label: t('LawField'),
      type: 'select' as const,
      value: selectedLawField.value,
      options: lawFieldOptions.value.map((option) => ({
        label: option.title,
        value: option.value
      }))
    }
  ])

  watch([selectedLawField, selectedClassifications], () => {
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  })

  const fetchCalls = async (options?: FetchDataOptions): Promise<PaginatedResponse<TCall>> => {
    try {
      isLoading.value = true
      const lawFieldFilter = options?.filters?.find((f) => f.key === 'lawField')
      const classificationFilter = options?.filters?.find((f) => f.key === 'classification')

      if (lawFieldFilter) {
        selectedLawField.value = lawFieldFilter.value || ''
      }
      if (classificationFilter) {
        selectedClassifications.value = classificationFilter.value || ''
      }
      searchQuery.value = options?.search || ''

      const params: any = {
        limit: options?.limit,
        offset: options?.offset,
        search: options?.search,
        resultDataLawField: lawFieldFilter?.value || selectedLawField.value,
        hasResult: true
      }

      // Handle multi-select classification filter (comma-separated values)
      if (classificationFilter?.value) {
        params.resultClassification = classificationFilter.value
      }

      const response = await CallsService.callsList(params)

      totalCalls.value = response.count || 0

      return response as unknown as PaginatedResponse<TCall>
    } catch (error) {
      console.error('Error fetching calls:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchCurrentOrg = async () => {
    try {
      const [orgResponse, userResponse] = await Promise.all([
        OrganisationsService.organisationsRetrieve({ uuid: 'current' }),
        UsersService.usersRetrieve({ uuid: 'current' })
      ])

      org.value = orgResponse as CustomerOrganisation

      isVoiceStandAlone.value =
        !org.value.enable_cases || (userResponse as any).role === RoleEnum.PHONE_AI_USER
    } catch (error) {
      console.error('Error fetching orgs:', error)
    }
  }

  const fetchPhoneNumbers = async () => {
    try {
      const response = await PhoneNumbersService.phoneNumbersList()
      phoneNumbers.value = response.results as CustomerPhoneNumber[]
    } catch (error) {
      console.error('Error fetching phone numbers:', error)
    }
  }

  const handleGoToSettings = () => {
    router.push(ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_NUMBERS)
  }

  const handlePhonebotSetup = () => {
    handleGoToSettings()
  }

  onMounted(() => {
    fetchCurrentOrg()
    fetchPhoneNumbers()
  })

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleJumpToCase = (call: TCall) => {
    if (call.case_uuid) {
      window.location.href = ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', call.case_uuid)
    }
  }

  const handleViewDetails = (call: TCall) => {
    router.push(`/app/calls/${call.uuid}`)
  }

  const getStatusColor = (result: CustomerCallResult) => {
    if (!result) return 'bg-gray-50 text-gray-700 ring-gray-600/20'

    switch (result.classification) {
      case ClassificationEnum.EXISTING_CLIENT_CALL_BACK:
        return 'bg-purple-50 text-purple-700 ring-purple-600/20'

      case ClassificationEnum.NEW_CLIENT_CALL:
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'

      case ClassificationEnum.GENERAL_ENQUIRY:
        return 'bg-sky-50 text-sky-700 ring-sky-600/20'

      case ClassificationEnum.QUESTION:
        return 'bg-amber-50 text-amber-700 ring-amber-600/20'

      case ClassificationEnum.NOT_COMPLETED:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'

      case ClassificationEnum.LAWFIRM_PARTNER:
        return 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'

      case ClassificationEnum.WITHOUT_MESSAGE:
        return 'bg-red-50 text-red-700 ring-red-600/20'

      case ClassificationEnum.COURT:
        return 'bg-red-100 text-red-800 ring-red-700/30'

      case ClassificationEnum.SPAM:
        return 'bg-gray-100 text-gray-800 ring-gray-700/30'

      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'
    }
  }

  const classificationLabels = {
    EXISTING_CLIENT_CALL_BACK: `${t('ExistingClient')}`,
    NEW_CLIENT_CALL: `${t('NewClient')}`,
    GENERAL_ENQUIRY: `${t('GeneralEnquiry')}`,
    QUESTION: `${t('Question')}`,
    NOT_COMPLETED: `${t('NotCompleted')}`,
    LAWFIRM_PARTNER: `${t('LawFirmPartner')}`,
    CALL_FORWARDED: `${t('CallForwarded')}`,
    WITHOUT_MESSAGE: `${t('WithoutMessage')}`,
    COURT: `${t('Court')}`,
    SPAM: `${t('Spam')}`
  }

  type ClassificationType = keyof typeof classificationLabels

  const getClassificationLabel = (classification: ClassificationType | undefined) => {
    if (!classification) return t('Unknown')
    const resp = classificationLabels[classification]
    return resp
  }

  // Direct deletion function
  const deleteCall = async (call: TCall) => {
    try {
      await CallsService.callsDestroy({
        uuid: call.uuid
      })

      // Refresh the table data
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error deleting call:', error)
    }
  }

  // get clerk for the call. Case clerk is preferred
  const getCallClerk = (call: TCall) => {
    return call.case?.clerk || call.clerk || null
  }
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen pb-20 pt-5">
      <div class="mx-auto space-y-6">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900">{{ t('Calls') }}</h1>
            <p class="mt-1 text-sm text-gray-500">{{ t('pageDescription') }}</p>
          </div>

          <!-- Phone Numbers Display -->
          <div
            v-if="phoneNumbers.length > 0"
            class="flex flex-col items-end gap-1"
          >
            <span class="text-sm font-medium text-gray-600">
              {{ phoneNumbers.length === 1 ? t('YourPhoneNumber') : t('YourPhoneNumbers') }}
            </span>
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <span
                  v-for="(phoneNumber, index) in phoneNumbers"
                  :key="phoneNumber.uuid"
                  class="flex items-center gap-2 text-sm font-medium text-gray-900"
                >
                  {{ phoneNumber.phone_number }}
                  <span
                    v-if="index < phoneNumbers.length - 1"
                    class="text-gray-400"
                  >
                    •
                  </span>
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                class="h-8 w-8"
                :title="t('GoToSettings')"
                @click="handleGoToSettings"
              >
                <Settings class="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5">
          <!-- Show paginated table if not standalone or loading or there are calls or there are no calls and there are filters-->
          <PaginatedTableView
            v-if="
              !isVoiceStandAlone ||
              isLoading ||
              (totalCalls !== null && totalCalls > 0) ||
              (totalCalls === 0 &&
                (selectedLawField !== '' || selectedClassifications !== '' || searchQuery !== ''))
            "
            ref="tableRef"
            :fetchData="fetchCalls"
            searchable-fields="Name"
            :filterable-fields="filterableFields"
            :initial-page="initialPage"
            @page-change="handlePageChange"
          >
            <template #table-head>
              <TableCell class="w-[140px] py-4">{{ t('TimeStamp') }}</TableCell>
              <TableCell class="w-[140px] py-4">{{ t('Type') }}</TableCell>
              <TableCell class="w-[180px] py-4">Name</TableCell>
              <TableCell class="w-[160px] py-4">{{ t('Phone') }}</TableCell>
              <TableCell class="w-[120px] py-4">{{ t('FieldTopic') }}</TableCell>
              <TableCell class="w-[100px] py-4">{{ t('Duration') }}</TableCell>
              <TableCell class="w-[160px] py-4">{{ t('Clerk') }}</TableCell>
              <TableCell
                v-if="!isVoiceStandAlone"
                class="py-4"
              >
                {{ t('ViewInquiry') }}
              </TableCell>
              <TableCell class="py-4"></TableCell>
            </template>

            <template #table-row="{ items }">
              <TableRow
                v-for="call in items"
                :key="call.uuid"
                class="group cursor-pointer border-b border-gray-100 hover:bg-gray-50/50"
                @click="
                  call.case_uuid && !isVoiceStandAlone
                    ? handleJumpToCase(call)
                    : handleViewDetails(call)
                "
              >
                <TableCell class="whitespace-nowrap py-4 text-sm text-gray-600">
                  {{ formatDate(call.call_start_time) }}
                </TableCell>
                <TableCell>
                  <Badge :class="`${getStatusColor(call.result)} whitespace-nowrap`">
                    {{ getClassificationLabel(call.result?.classification) || t('Unknown') }}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div class="font-medium text-gray-900">
                    {{
                      `${call.result?.data?.client?.first_name ?? ''} ${call.result?.data?.client?.last_name ?? ''}`.trim() ||
                      '_'
                    }}
                  </div>
                </TableCell>
                <TableCell class="whitespace-nowrap">
                  <div class="flex items-center gap-2 text-sm text-gray-600">
                    <Phone class="h-3 w-3 text-gray-400" />
                    {{ call.result?.data?.client?.phone || '-' }}
                  </div>
                </TableCell>
                <TableCell class="text-xs text-gray-600">
                  {{ translateLawField(call.result?.data?.law?.field) }}
                  <br />
                  {{ translateLawTopic(call.result?.data?.law?.topic) }}
                </TableCell>
                <TableCell class="text-sm text-gray-600">
                  {{ formatDuration(call.duration) }}
                </TableCell>
                <TableCell>
                  <div
                    v-if="getCallClerk(call)"
                    class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1"
                  >
                    <div
                      class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-sm text-white"
                    >
                      {{ getCallClerk(call)?.first_name?.charAt(0) || '' }}
                    </div>
                    <span class="truncate text-sm">
                      {{ getCallClerk(call)?.first_name + ' ' + getCallClerk(call)?.last_name }}
                    </span>
                  </div>
                  <span
                    v-else
                    class="text-sm text-gray-500"
                  >
                    {{ t('notAssigned') }}
                  </span>
                </TableCell>
                <TableCell
                  v-if="!isVoiceStandAlone"
                  class="pr-4"
                >
                  <div class="flex items-center justify-end gap-2">
                    <!-- Only show indicator when there's a case_uuid -->
                    <Button
                      v-if="call.case_uuid"
                      variant="secondary"
                      class="flex h-8 min-w-[122px] items-center gap-1 border border-gray-300 bg-white text-sm hover:bg-gray-200"
                      @click.stop="handleJumpToCase(call)"
                    >
                      <ExternalLink class="h-3 w-3" />
                      {{ t('Inquiry') }}
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <!-- Dropdown menu for other actions -->
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        class="h-8 w-8"
                        @click.stop
                      >
                        <MoreVertical class="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <!-- Delete option for all calls -->
                      <DropdownMenuItem
                        class="text-red-500 focus:bg-red-50 focus:text-red-600"
                        @click="deleteCall(call)"
                      >
                        <Trash2 class="mr-2 h-4 w-4" />
                        {{ t('Delete') }}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            </template>
          </PaginatedTableView>
          <div
            v-else-if="isVoiceStandAlone && !isLoading"
            class="flex flex-col items-center justify-center py-16"
          >
            <!-- Illustration -->
            <CustomerService class="mb-8 h-64 w-64" />

            <!-- Title -->
            <h2 class="mb-3 text-center text-xl font-semibold text-gray-900">
              {{ t('emptyStateTitle') }}
            </h2>

            <!-- Description -->
            <p
              class="mb-8 max-w-md whitespace-pre-line text-center text-base font-normal leading-6 tracking-normal
                text-gray-600"
            >
              {{ t('emptyStateDescription') }}
            </p>

            <!-- Setup Button -->
            <Button
              size="lg"
              class="bg-purple-600 hover:bg-purple-700"
              @click="handlePhonebotSetup"
            >
              {{ t('setupNow') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </SpaCompatComponent>

  <!-- Setup Phonebot Dialog -->
  <PhonebotSetupDialog
    v-if="isVoiceStandAlone"
    :total-calls="totalCalls"
    @setup="handlePhonebotSetup"
  />
</template>
